import { useEffect, useState } from "react";
import { FoundPhysicalAddressesEntry, knxReadBack } from "./KnxReadback";
import KnxBridge, { DeviceNotReachable, knxbridgeEmitter, refreshUI } from "../KnxBridge";
import { useOnChangeWatcher } from "../models/useOnChangeWatcher";
import React from "react";
import ReadBack from "./ReadBack";
import BaseDialog, { Field, FieldSet } from "../modelDialogs/BaseDialog";
import { itx } from "../translationItx/itx";
import { openProgrammingPane } from "../components/Menubar";
import ReadBackViaKnxBridge from "./ReadBackViaKnxBridge";
import { gatewayIp, bridgeOn } from "../KnxBridge";
import { updateToastProgress } from "../components/Toasts";
import BridgeStatus from "./BridgeStatus";

export enum readMode {
  listen = 0,
  linescan = 1,
  areascan = 2, // Not in use currently
  input = 3,
}

export default function ReadbackDeviceDialog(props: any) {
  const {} = props;
  const [gatewayIP, setGatewayIP] = useState(gatewayIp);
  const [bridgeOnline, setBridgeOnline] = useState(bridgeOn);
  const [listMode, setListMode] = useState(readMode.listen);
  const [scanning, setScanning] = useState(false);
  const [scannedlist, setScannedList] = useState<Array<FoundPhysicalAddressesEntry>>([]);
  const [inputline, setInputLine] = useState("");
  const [currentSegment, setCurrentSegment] = useState(0);

  const rb = knxReadBack();

  useEffect(() => {
    // triggers rerender only when IP changes ; consumes less cpu resources than update via ping
    const ipHandler = (newIp: string) => {
      setGatewayIP(newIp);
    };
    const bridgeHandler = (status: boolean) => {
      setBridgeOnline(status);
    };

    knxbridgeEmitter.on("ipChange", ipHandler);
    knxbridgeEmitter.on("bridgeChange", bridgeHandler);

    rb.readbackMonitoringIsRunning = true;
    openProgrammingPane();
    KnxBridge.start();
    return () => {
      knxbridgeEmitter.off("ipChange", ipHandler); // clean up
      knxbridgeEmitter.off("bridgeChange", bridgeHandler);
      KnxBridge.stop();
    };
  }, []);

  useOnChangeWatcher(rb.watchModified);

  const addressInput = React.createRef<HTMLInputElement>();
  const areaLineInput = React.createRef<HTMLInputElement>();

  const alreadyImportedDevices = new Array<string>();
  state.allDevices().forEach((d) => alreadyImportedDevices.push(d.physicalAddress));

  // let sortedfoundPhysicalAddresses = rb.foundPhysicalAddresses.sort((a, b) =>
  //   addrStringToInt(a.addr) < addrStringToInt(b.addr) ? -1 : 1);
  let sortedfoundPhysicalAddresses = rb.foundPhysicalAddresses.sort((a, b) => {
    const [ax, ay, az] = a.addr.split(".").map(Number);
    const [bx, by, bz] = b.addr.split(".").map(Number);

    // compare x, then y and z in order
    if (ax !== bx) return ax - bx;
    if (ay !== by) return ay - by;
    return az - bz;
  });

  let sortedLineScannedAddresses = rb.lineScannedAddresses.sort((a, b) => {
    const [ax, ay, az] = a.addr.split(".").map(Number);
    const [bx, by, bz] = b.addr.split(".").map(Number);

    // compare x, then y and z in order
    if (ax !== bx) return ax - bx;
    if (ay !== by) return ay - by;
    return az - bz;
  });

  //
  // Filter out all devices already imported
  //
  // sortedfoundPhysicalAddresses = sortedfoundPhysicalAddresses.filter( a => alreadyImportedDevices.indexOf(a.addr) < 0);

  const deviceElementsDiscovered = sortedfoundPhysicalAddresses.map((item, index) => {
    // const isCreated =  alreadyImportedDevices.indexOf(item.addr) > -1;
    const isCreated = state.allDevices().findLast((d) => d.fullPhysicalAddress == item.addr);
    return (
      <tr key={index} style={{ backgroundColor: index % 2 === 0 ? "#f0f0f0" : "white" }}>
        <td style={{ verticalAlign: "middle" }} key={item.addr}>
          {" "}
          {item.addr}{" "}
        </td>
        {!isCreated && (
          <td>
            <button
              className={"button"}
              onClick={async () => {
                await importDevice(item.addr, false, true);
              }}
            >
              {itx("readback.create", `Create`)}
            </button>
          </td>
        )}

        {isCreated && <td style={{ verticalAlign: "middle" }}>{itx("readback.created", `Created`)}</td>}

        <td>
          {
            <button
              className={"button"}
              onClick={async () => {
                await importDevice(item.addr, false, false);
              }}
            >
              {itx("readback.import", `Import`)}
            </button>
          }
        </td>
      </tr>
    );
  });

  const deviceElementsLineScanned = sortedLineScannedAddresses.map((item, index) => {
    // const isCreated =  alreadyImportedDevices.indexOf(item.addr) > -1;
    const isCreated = state.allDevices().findLast((d) => d.fullPhysicalAddress == item.addr);
    return (
      <tr key={index} style={{ backgroundColor: index % 2 === 0 ? "#f0f0f0" : "white", verticalAlign: "middle" }}>
        <td style={{ verticalAlign: "middle" }} key={item.addr}>
          {" "}
          {item.addr}{" "}
        </td>
        <td style={{ verticalAlign: "middle" }}> {item.mask ? item.mask : ""} </td>
        {!isCreated && (
          <td>
            <button
              className={"button"}
              onClick={async () => {
                await importDevice(item.addr, false, true);
              }}
            >
              {itx("readback.create", `Create`)}
            </button>
          </td>
        )}
        {isCreated && <td style={{ verticalAlign: "middle" }}>{itx("readback.created", `Created`)}</td>}

        <td>
          {
            <button
              className={"button"}
              onClick={async () => {
                await importDevice(item.addr, false, false);
              }}
            >
              {itx("readback.import", `Import`)}
            </button>
          }
        </td>
      </tr>
    );
  });

  function addrStringToInt(a: string): number {
    const ss = a.split(".");
    return (parseInt(ss[0]) * 16 + parseInt(ss[1])) * 16 + parseInt(ss[2]);
  }

  async function importDeviceFromInput(createOnly: boolean) {
    if (addressInput.current) {
      const deviceAddress = addressInput.current.value.trim();
      await importDevice(deviceAddress, false, createOnly);
    }
  }

  async function importDevice(deviceAddress: string, batchImport: boolean, createOnly: boolean): Promise<boolean> {
    let res = false;
    if (deviceAddress && KnxBridge.isPhysicalAddressValid(deviceAddress)) {
      if (!batchImport) {
        rb.setToastText(`Creating Device with Address: ${deviceAddress}`, `Looking for device in network...`);
      }
      const device = state.allDevices().findLast((item) => item.fullPhysicalAddress == deviceAddress);
      if (createOnly) {
        if (device) {
          // Already created device, no further operation
          if (!batchImport)
            rb.setToastText(rb.toastTitle, `${itx("knxBridge.deviceAlreadyCreated", `Device Already Created!`)}`);
          res = true;
        } else {
          // Only Create new device in Building structure, no readback
          res = await ReadBack.readBackDeviceUsingAddress(deviceAddress, false);
        }
      } else {
        if (device) {
          // Already created device, only readback memory
          res = await ReadBackViaKnxBridge.readbackDeviceMemory(device);
        } else {
          // Create device then readback memory
          res = await ReadBack.readBackDeviceUsingAddress(deviceAddress, true);
        }
      }
      if (!batchImport) {
        rb.setToastText(`Device Creation/Readback Finished.`, rb.toastText, false);
        refreshUI();
      }
    } else {
      rb.setToastText(``, `${itx("knxBridge.readBackInvalidAddress", `Please enter valid device Address.`)}`, false);
    }
    return res;
  }

  async function importAllDevices(createOnly: boolean) {
    // for(let deviceAddress of alreadyImportedDevices){
    //   await importDevice(deviceAddress, true, createOnly);
    // }
    const addressList = new Array<string>();
    rb.foundPhysicalAddresses.forEach(async (item) => {
      addressList.push(item.addr);
    });

    let index = 1;
    let countDevicesRbOrCreated = 0;
    const errorDeviceList = new Array<string>();
    rb.progress = true;
    for (let addr of addressList) {
      const percent = Math.floor((index * 100) / addressList.length);
      rb.setToastText(
        `Creating Device ${index} of ${addressList.length}; Address: ${addr}`,
        `Looking for device in network...`,
        true,
      );
      const success = await importDevice(addr, true, createOnly);
      if (success) {
        countDevicesRbOrCreated++;
      } else {
        errorDeviceList.push(addr);
      }
      if (!createOnly) {
        alreadyImportedDevices.push(addr);
      }
      index++;
      updateToastProgress({ progress: percent });
    }
    refreshUI();
    showBatchStatistics(
      countDevicesRbOrCreated,
      addressList.length,
      errorDeviceList,
      createOnly ? "Create" : "Readback", // no itx
    );
    rb.progress = false;
    rb.setToastText(
      `Device Creation/Readback Finished.`,
      `${itx("knxBridge.devicesLoadCompleted", `All Devices successfully loaded into project.`)}`,
      false,
    );
  }

  async function scanLine() {
    if (areaLineInput.current) {
      const arealine = areaLineInput.current.value.trim();
      if (KnxBridge.isAreaLineValid(arealine)) {
        setInputLine(arealine);
        setScanning(true);
        rb.clearScannedAddrs();
        const [area, line] = arealine.split(".");
        const result = await KnxBridge.scanLine(area, line);
        const deviceAddresses = result
          .trim()
          .split("\n")
          .map((line) => {
            const [addr, mask] = line.split(";");
            return {
              addr: addr.trim(),
              mask: mask?.trim() || null,
              time: Date.now(),
            };
          });

        rb.setScannedAddrList(deviceAddresses);
        setScannedList(deviceAddresses);

        // commenting sequential line scan (high time complexity)
        // for(let segment = 0; segment < 256; segment++){
        //   if(!scanning) break;
        //   setCurrentSegment(segment);
        //   const fullAddr = `${arealine}.${segment}`;
        //   const res = await KnxBridge.isDevicePresentInNetwork(fullAddr);
        //   if(res != DeviceNotReachable) rb.registerScannedAddr(fullAddr);
        // }
        setCurrentSegment(0);
        setScanning(false);
        rb.setToastText(``, `${itx("knxBridge.lineScanComplete", `Line Scan Completed!`)}`, false);
      } else {
        rb.setToastText(
          ``,
          `${itx("knxBridge.readBackInvalidLine", `Please enter area line in correct format (example: 1.1)`)}`,
          false,
        );
      }
    }
  }

  function LoaderRenderer() {
    return (
      <div style={{ textAlign: "center", gridColumn: "1/-1" }}>
        <i className={`las la-spinner la-spin la-2x`}></i>
      </div>
    );
  }

  function EmptyRowsRenderer() {
    return (
      <div style={{ textAlign: "center", width: "100%" }}>
        {state.translate("readback.noDevicesToDisplay", "------ No Device Address to display ------")}
      </div>
    );
  }

  function showBatchStatistics(
    validDeviceCount: number,
    totalDevices: number,
    errorDeviceList: Array<string>,
    operation: string,
  ): Promise<void> {
    const title = itx("knxBridge.batchStats", "Batch Device Operation Statistics").replace("Operation", operation);
    const pastTense =
      operation == "Create" // no itx
        ? "created" // no itx
        : operation == "Readback"
          ? "readback" // no itx
          : "operated"; // no itx

    return new Promise<void>((resolve, reject) => {
      state.dialogManager.openDialog(
        <BaseDialog
          title={title}
          abortCaption={itx("close", "Schließen")}
          noOkButton={true}
          onOk={() => {
            resolve();
            return true;
          }}
        >
          {
            <table style={{ textAlign: "left" }}>
              <tr>
                <td>
                  <b>{itx("knxBridge.status", "STATUS:")}</b>
                </td>
                <td>
                  <b>{itx("knxBridge.readbackOperationComplete", "Batch Operation Completed")}</b>
                </td>
              </tr>
              <tr>
                <td>{`Total devices ${pastTense}`}</td>
                <td>{`${validDeviceCount} ${validDeviceCount == totalDevices ? "(All)" : ""}`}</td>
              </tr>
              <tr>
                <td>{`Total devices not ${pastTense} (Operation not applicable)`}</td>
                <td>{`${totalDevices - validDeviceCount} ${validDeviceCount == 0 ? "(All)" : ""}`}</td>
              </tr>
              {totalDevices - validDeviceCount > 0 && (
                <tr>
                  <td>{`List of device addresses not ${pastTense}:`}</td>
                  <td>{errorDeviceList.join(", ")}</td>
                </tr>
              )}
            </table>
          }
        </BaseDialog>,
        true,
      );
    });
  }

  return (
    <BaseDialog
      title={itx("inlineButton.readbackDevice", "Readback Device")}
      onOk={() => {
        state.dialogManager.closeDialog();
        return true;
      }}
      okCaption={state.translate("ok", "Ok")}
    >
      <div style={{ padding: "10px" }}>
        {!gatewayIP && !bridgeOnline && <BridgeStatus />}
        {!gatewayIP && bridgeOnline && (
          <div>{`${itx("knxBridge.gatewaydisconnectedNw", "⚠️ Gateway is active but not connected to the network.")} ${itx("knxBridge.restartGateway", "If it doesn't reconnect automatically, try restarting the gateway.")}`}</div>
        )}
        {gatewayIP && (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div>
              {itx("knxBridge.connectGateewayIp", "Connected gateway IP:")}&nbsp;
              <b>{gatewayIP}</b>
            </div>
            <div>
              {itx("readback.discoverMode", "Device Discovery Mode: ")}
              <b>{`${listMode == readMode.listen ? itx("readback.autodiscover", "Listen and Auto-Discover") : itx("readback.linescan", "Line Scan")}`}</b>
              &nbsp;
              <span>
                <button
                  className={"button"}
                  disabled={scanning}
                  onClick={() => {
                    const mode = listMode == readMode.listen ? readMode.linescan : readMode.listen;
                    setListMode(mode);
                  }}
                >
                  {`${itx("readback.switchto", "Switch to ")} ${listMode == readMode.linescan ? itx("readback.autodiscover", "Listen and Auto-Discover") : itx("readback.linescan", "Line Scan")}`}{" "}
                </button>
              </span>
            </div>

            {listMode == readMode.listen && (
              <div>
                {itx("readback.devicesCurrentlyOnline", "Devices Currently Online (Auto-Refreshing)")}:
                {/* <LoaderRenderer /> */}
                <div
                  style={{
                    margin: "9px 5px",
                    height: "300px",
                    overflow: "auto",
                    padding: "3px",
                    boxShadow: "0 0 0.25cm rgba(0, 0, 0, 0.5)",
                    border: "1px solid grey",
                  }}
                >
                  {deviceElementsDiscovered.length == 0 ? (
                    <EmptyRowsRenderer />
                  ) : (
                    <table style={{ width: "-webkit-fill-available" }}>{deviceElementsDiscovered}</table>
                  )}
                </div>
                <div style={{ marginTop: "5px", display: "flex", flexDirection: "column" }}>
                  <button
                    className={"button"}
                    onClick={async () => {
                      await importAllDevices(true);
                    }}
                  >
                    {itx("readback.createAllDevices", "Create All Devices")}{" "}
                  </button>
                  <button
                    className={"button"}
                    onClick={async () => {
                      await importAllDevices(false);
                    }}
                  >
                    {itx("readback.readbackAllDevices", "Readback All Devices")}
                  </button>
                </div>
              </div>
            )}

            {listMode == readMode.linescan && (
              <div>
                <div>
                  <span>{itx("readback.line", "Line")}</span>&nbsp;&nbsp;
                  <input
                    ref={areaLineInput}
                    id="line_value"
                    className="input"
                    maxLength={state.maxCharLimit.address}
                    defaultValue={""}
                    disabled={scanning}
                  />
                  <button
                    className={"button"}
                    disabled={scanning}
                    onClick={async () => {
                      await scanLine();
                    }}
                  >
                    {scanning ? itx("readback.scanning", "Scanning") : itx("readback.scan", "Scan")}
                    {scanning && (
                      <span className="appTextSuffix rightAlign">
                        <i className={`las la-spinner la-spin`}></i>
                      </span>
                    )}
                  </button>
                  <button
                    className={"button"}
                    disabled={!scanning}
                    onClick={() => {
                      setScanning(false);
                    }}
                  >
                    {itx("readback.stopscan", "Stop")}{" "}
                  </button>
                </div>
                {scanning && <div>{`${itx("readback.devicesInLine", "Active Devices in Line: ")} ${inputline}`}</div>}
                {/* (Scanning segment ${currentSegment}/255)... */}
                {/* <LoaderRenderer /> */}
                <div
                  style={{
                    margin: "9px 5px",
                    height: "300px",
                    overflow: "auto",
                    padding: "3px",
                    boxShadow: "0 0 0.25cm rgba(0, 0, 0, 0.5)",
                    border: "1px solid grey",
                  }}
                >
                  {deviceElementsLineScanned.length == 0 ? (
                    <EmptyRowsRenderer />
                  ) : (
                    <table style={{ width: "-webkit-fill-available" }}>{deviceElementsLineScanned}</table>
                  )}
                </div>

                <div style={{ marginTop: "5px", display: "flex", flexDirection: "column" }}>
                  <button
                    className={"button"}
                    onClick={async () => {
                      await importAllDevices(true);
                    }}
                  >
                    {itx("readback.createAllDevices", "Create All Devices")}{" "}
                  </button>
                  <button
                    className={"button"}
                    onClick={async () => {
                      await importAllDevices(false);
                    }}
                  >
                    {itx("readback.readbackAllDevices", "Readback All Devices")}
                  </button>
                </div>
              </div>
            )}

            <div style={{ marginTop: "5px", textAlign: "center" }}> {itx("ReadbackDeviceDialog.or", "---- OR ----")} </div>

            <FieldSet>
              <Field label={state.translate("knxBridge.inputKnownAddress", "Input Known Address")}>
                <input
                  ref={addressInput}
                  id="deviceAddress_value"
                  className="input"
                  maxLength={state.maxCharLimit.address}
                  defaultValue={""}
                  disabled={!gatewayIP}
                />
              </Field>
            </FieldSet>

            <div style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <button
                className={"button"}
                onClick={async () => {
                  await importDeviceFromInput(true);
                }}
              >
                {" "}
                {itx("readback.createDeviceOnly", "Create Device In Project")}
              </button>
              <button
                className={"button"}
                onClick={async () => {
                  await importDeviceFromInput(false);
                }}
              >
                {itx("readback.createAndReadback", "Create and Readback")}
              </button>
            </div>
          </div>
        )}
      </div>
    </BaseDialog>
  );
}
